import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2 } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  code: "",
  sort_order: "0",
  printer_id: "",
  is_active: true,
};

function apiError(err, fallback) {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat()[0];
  return err.response?.data?.message || fallback;
}

export default function KotMaster() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const branchId = user?.default_branch?.id;

  const { data: stations = [], isLoading } = useQuery({
    queryKey: ["kitchen-stations", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/kitchen-stations", { params: { branch_id: branchId } });
      return res.stations || [];
    },
    enabled: Boolean(branchId),
  });

  const { data: printers = [] } = useQuery({
    queryKey: ["kitchen-printers", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/printers", {
        params: { branch_id: branchId, type: "Kitchen" },
      });
      return res.printers || [];
    },
    enabled: Boolean(branchId),
  });

  const rows = stations.filter((s) =>
    `${s.name} ${s.code || ""}`.toLowerCase().includes(search.toLowerCase()),
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        sort_order: Number(form.sort_order) || 0,
        printer_id: form.printer_id ? Number(form.printer_id) : null,
        is_active: form.is_active,
      };
      if (editingId) {
        return api.put(`/kitchen-stations/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/kitchen-stations", payload, { params: { branch_id: branchId } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-stations", branchId] });
      toast.success(res.data.message);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setView("form");
    },
    onError: (err) => toast.error(apiError(err, "Failed to save KOT")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/kitchen-stations/${id}`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-stations", branchId] });
      toast.success(res.data.message || "KOT deleted.");
    },
    onError: (err) => toast.error(apiError(err, "Failed to delete KOT")),
  });

  function openNew() {
    setEditingId(null);
    const next = stations.filter((s) => /^KOT\d+$/i.test(s.code || "")).length + 1;
    setForm({ ...EMPTY_FORM, code: `KOT${next}`, name: `KOT ${next}` });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      code: row.code || "",
      sort_order: String(row.sort_order ?? 0),
      printer_id: row.printer_id ? String(row.printer_id) : "",
      is_active: row.is_active !== false,
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    if (!form.name.trim()) {
      toast.error("KOT name is required.");
      return;
    }
    if (!form.code.trim()) {
      toast.error("Code is required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="KOT Master"
      subtitle={`Kitchen tickets (KOT1, KOT2, …) — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      onAddNew={openNew}
      testIdPrefix="kot-master"
      formTitle={editingId ? form.name || form.code || "Edit KOT" : "New KOT"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="kot-master"
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DataTable
              testIdPrefix="kot-master"
              emptyText="No KOTs yet"
              onRowClick={openEdit}
              columns={[
                { key: "code", label: "Code", mono: true, bold: true },
                { key: "name", label: "Name", bold: true },
                {
                  key: "printer",
                  label: "Printer",
                  render: (v) => v?.name || "—",
                },
                { key: "items", label: "Items", align: "right" },
                { key: "sort_order", label: "Sort", align: "right" },
                {
                  key: "status",
                  label: "Status",
                  render: (v) => <Pill tone={v === "Active" ? "green" : "default"}>{v}</Pill>,
                },
              ]}
              rows={rows}
              rowActions={(row) => [
                { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                {
                  title: "Delete",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  onClick: () => {
                    if (Number(row.items) > 0) {
                      toast.error("Reassign menu items before deleting this KOT.");
                      return;
                    }
                    if (window.confirm(`Delete KOT "${row.code}"?`)) {
                      deleteMutation.mutate(row.id);
                    }
                  },
                },
              ]}
            />
          )}
        </>
      }
    >
      <div className="space-y-4 max-w-md">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">Code *</Label>
            <Input
              value={form.code}
              onChange={(e) => setField("code", e.target.value)}
              className="h-9 font-mono uppercase"
              placeholder="KOT1"
              data-testid="kot-code"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">Sort order</Label>
            <Input
              value={form.sort_order}
              onChange={(e) => setField("sort_order", e.target.value)}
              className="h-9"
              type="number"
              min="0"
            />
          </div>
        </div>
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="h-9"
            placeholder="KOT 1"
            data-testid="kot-name"
          />
        </div>
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Kitchen printer</Label>
          <Select
            value={form.printer_id || "none"}
            onValueChange={(v) => setField("printer_id", v === "none" ? "" : v)}
          >
            <SelectTrigger className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {printers.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-white max-w-xs">
          <Switch checked={form.is_active} onCheckedChange={(v) => setField("is_active", v)} />
          <span className="text-[12px] text-slate-700">Active</span>
        </div>
      </div>
    </MasterCrudShell>
  );
}
